import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { 
  Calendar, Clock, Video, MapPin, ArrowLeft, Sparkles,
  CheckCircle, ChevronRight, User, HeartHandshake
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const TIMES = ['morning', 'afternoon', 'evening'];

export default function SmartScheduling() {
  const { clinicianId } = useParams();
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [clinician, setClinician] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [step, setStep] = useState(1);
  const [preferences, setPreferences] = useState({
    preferred_days: [],
    preferred_times: ['morning', 'afternoon'],
    duration_minutes: 60,
    appointment_type: 'both'
  });
  const [notes, setNotes] = useState('');
  const [bookingFor, setBookingFor] = useState('self'); // 'self' | 'loved_one'
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');

  useEffect(() => {
    if (clinicianId) {
      fetchClinicianInfo();
    }
  }, [clinicianId]);

  const fetchClinicianInfo = async () => {
    try {
      // Get clinician from matches
      const response = await axios.get(`${API}/patient/matches`, getAuthHeader());
      const match = response.data.find(m => m.clinician_id === clinicianId);
      if (match) {
        setClinician(match.clinician);
      }
    } catch (error) {
      console.error('Error fetching clinician:', error);
    }
  };

  const toggleDay = (day) => {
    setPreferences(prev => ({
      ...prev,
      preferred_days: prev.preferred_days.includes(day)
        ? prev.preferred_days.filter(d => d !== day)
        : [...prev.preferred_days, day]
    }));
  };

  const toggleTime = (time) => {
    setPreferences(prev => ({
      ...prev,
      preferred_times: prev.preferred_times.includes(time)
        ? prev.preferred_times.filter(t => t !== time)
        : [...prev.preferred_times, time]
    }));
  };

  const findSlots = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/scheduling/smart-slots/${clinicianId}`,
        preferences,
        getAuthHeader()
      );
      setSlots(response.data.slots);
      setStep(2);
    } catch (error) {
      toast.error('Failed to find available slots');
    } finally {
      setLoading(false);
    }
  };

  const bookAppointment = async () => {
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/scheduling/book`, {
        clinician_id: clinicianId,
        date: selectedSlot.date,
        time: selectedSlot.time,
        appointment_type: preferences.appointment_type === 'both' ? 'telehealth' : preferences.appointment_type,
        duration_minutes: preferences.duration_minutes,
        notes: notes,
        care_recipient_name: bookingFor === 'loved_one' ? (recipientName || undefined) : undefined,
        care_recipient_phone: bookingFor === 'loved_one' ? (recipientPhone || undefined) : undefined,
      }, getAuthHeader());

      if (bookingFor === 'loved_one' && recipientPhone) {
        toast.success(`Appointment booked — we've texted the visit link to ${recipientName || 'your loved one'}.`);
      } else {
        toast.success('Appointment booked successfully!');
      }
      setStep(3);
    } catch (error) {
      toast.error('Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-cn-sage-100 dark:from-cn-ink-900 dark:via-cn-ink-900 dark:to-cn-ink-800 py-8 px-4" data-testid="smart-scheduling">
      <div className="max-w-2xl mx-auto">
        <button 
          onClick={() => navigate('/patient/dashboard')}
          className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-cn-primary dark:hover:text-cn-primary mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-cn-primary to-cn-sage-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-display">Smart Scheduling</h1>
          <p className="text-slate-600 dark:text-slate-400">AI-optimized appointment booking</p>
        </div>

        {clinician && (
          <Card className="border-0 shadow-sm bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-cn-sage-100 dark:bg-cn-sage-900/30 rounded-2xl flex items-center justify-center">
                  <span className="text-lg font-bold text-cn-primary dark:text-cn-primary">
                    {clinician.first_name?.[0]}{clinician.last_name?.[0]}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 dark:text-white">
                    {clinician.first_name} {clinician.last_name}
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Healthcare Provider</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Preferences */}
        {step === 1 && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <Calendar className="w-5 h-5 text-cn-primary" />
                Your Preferences
              </CardTitle>
              <CardDescription>Help us find the best times for you</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Who is this visit for? */}
              <div data-testid="booking-for-section">
                <label className="text-sm font-medium text-slate-900 dark:text-white mb-3 block">Who is this visit for?</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setBookingFor('self')}
                    data-testid="booking-for-self"
                    aria-pressed={bookingFor === 'self'}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      bookingFor === 'self'
                        ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                        : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                    }`}
                  >
                    <User className={`w-5 h-5 mb-2 ${bookingFor === 'self' ? 'text-cn-primary' : 'text-slate-400'}`} />
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Myself</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">I'm the one receiving care</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBookingFor('loved_one')}
                    data-testid="booking-for-loved-one"
                    aria-pressed={bookingFor === 'loved_one'}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      bookingFor === 'loved_one'
                        ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                        : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                    }`}
                  >
                    <HeartHandshake className={`w-5 h-5 mb-2 ${bookingFor === 'loved_one' ? 'text-cn-primary' : 'text-slate-400'}`} />
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">A loved one</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">I'm booking on their behalf</p>
                  </button>
                </div>

                {bookingFor === 'loved_one' && (
                  <div className="mt-4 space-y-3 p-4 rounded-xl bg-slate-50 dark:bg-cn-ink-700/40 border border-slate-200 dark:border-cn-ink-700" data-testid="recipient-fields">
                    <div>
                      <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Their name</label>
                      <Input
                        data-testid="recipient-name-input"
                        placeholder="e.g. Mom, or Rose"
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">Their mobile number (optional)</label>
                      <Input
                        data-testid="recipient-phone-input"
                        placeholder="+1 555 123 4567"
                        value={recipientPhone}
                        onChange={(e) => setRecipientPhone(e.target.value)}
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
                        We'll text them a simple, no-login link to see and join their visit.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Preferred Days */}
              <div>
                <label className="text-sm font-medium text-slate-900 dark:text-white mb-3 block">Preferred Days</label>
                <div className="flex flex-wrap gap-2">
                  {DAYS.map((day) => (
                    <button
                      key={day}
                      onClick={() => toggleDay(day)}
                      className={`px-4 py-2 rounded-full text-sm capitalize transition-all ${
                        preferences.preferred_days.includes(day)
                          ? 'bg-cn-primary text-white'
                          : 'bg-slate-100 dark:bg-cn-ink-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Leave empty for any day</p>
              </div>

              {/* Preferred Times */}
              <div>
                <label className="text-sm font-medium text-slate-900 dark:text-white mb-3 block">Preferred Times</label>
                <div className="grid grid-cols-3 gap-3">
                  {TIMES.map((time) => (
                    <button
                      key={time}
                      onClick={() => toggleTime(time)}
                      className={`p-4 rounded-xl border-2 transition-all capitalize ${
                        preferences.preferred_times.includes(time)
                          ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                          : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                      }`}
                    >
                      <Clock className={`w-5 h-5 mx-auto mb-2 ${
                        preferences.preferred_times.includes(time) ? 'text-cn-primary' : 'text-slate-400'
                      }`} />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{time}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Appointment Type */}
              <div>
                <label className="text-sm font-medium text-slate-900 dark:text-white mb-3 block">Visit Type</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'telehealth', label: 'Telehealth', icon: Video },
                    { value: 'in_person', label: 'In-Person', icon: MapPin },
                    { value: 'both', label: 'Either', icon: CheckCircle }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setPreferences({...preferences, appointment_type: type.value})}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        preferences.appointment_type === type.value
                          ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                          : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                      }`}
                    >
                      <type.icon className={`w-5 h-5 mx-auto mb-2 ${
                        preferences.appointment_type === type.value ? 'text-cn-primary' : 'text-slate-400'
                      }`} />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Duration */}
              <div>
                <label className="text-sm font-medium text-slate-900 dark:text-white mb-3 block">Duration</label>
                <div className="flex gap-3">
                  {[30, 45, 60, 90].map((mins) => (
                    <button
                      key={mins}
                      onClick={() => setPreferences({...preferences, duration_minutes: mins})}
                      className={`flex-1 py-3 rounded-xl border-2 transition-all ${
                        preferences.duration_minutes === mins
                          ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                          : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                      }`}
                    >
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{mins} min</span>
                    </button>
                  ))}
                </div>
              </div>

              <Button
                onClick={findSlots}
                disabled={loading}
                data-testid="find-slots-btn"
                className="w-full bg-gradient-to-br from-cn-primary to-cn-sage-500 hover:from-cn-primary hover:to-cn-sage-600 text-white rounded-xl h-12"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Finding best times...
                  </div>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Find Available Times
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Slot */}
        {step === 2 && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <Clock className="w-5 h-5 text-cn-primary" />
                Available Times
              </CardTitle>
              <CardDescription>Select your preferred appointment time</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {slots.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">No available slots found</p>
                  <Button
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="mt-4"
                  >
                    Adjust Preferences
                  </Button>
                </div>
              ) : (
                <>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {slots.map((slot, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedSlot(slot)}
                        data-testid={`slot-option-${index}`}
                        className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                          selectedSlot?.date === slot.date && selectedSlot?.time === slot.time
                            ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                            : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-slate-100 dark:bg-cn-ink-700 rounded-xl flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                          </div>
                          <div className="text-left">
                            <p className="font-semibold text-slate-900 dark:text-white">{slot.day}, {slot.date}</p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">{slot.time}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className="bg-cn-sage-100 text-cn-primary dark:bg-cn-sage-900/30 dark:text-cn-primary">
                            {slot.score}% match
                          </Badge>
                          <div className="flex gap-1">
                            {slot.available_types?.includes('telehealth') && (
                              <Video className="w-4 h-4 text-cn-primary" />
                            )}
                            {slot.available_types?.includes('in_person') && (
                              <MapPin className="w-4 h-4 text-green-500" />
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {selectedSlot && (
                    <div className="pt-4 border-t border-slate-200 dark:border-cn-ink-700">
                      <label className="text-sm font-medium text-slate-900 dark:text-white mb-2 block">
                        Notes for clinician (optional)
                      </label>
                      <Textarea
                        placeholder="Any specific concerns or topics you'd like to discuss..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="bg-slate-50 dark:bg-cn-ink-700/50"
                      />
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={() => setStep(1)}
                      variant="outline"
                      className="flex-1 rounded-xl h-12"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={bookAppointment}
                      disabled={!selectedSlot || loading}
                      data-testid="confirm-booking-btn"
                      className="flex-1 bg-gradient-to-br from-cn-primary to-cn-sage-500 hover:from-cn-primary hover:to-cn-sage-600 text-white rounded-xl h-12"
                    >
                      {loading ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          Confirm Booking
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white font-display mb-2">
                Appointment Booked!
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {bookingFor === 'loved_one'
                  ? `Your clinician has been notified.${recipientPhone ? ` We've texted ${recipientName || 'your loved one'} a no-login link to view and join their visit.` : ''}`
                  : 'Your clinician has been notified and will confirm shortly.'}
              </p>

              {selectedSlot && (
                <div className="bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl p-6 mb-6">
                  <div className="grid grid-cols-2 gap-4 text-left">
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Date</p>
                      <p className="font-semibold text-slate-900 dark:text-white">{selectedSlot.day}, {selectedSlot.date}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Time</p>
                      <p className="font-semibold text-slate-900 dark:text-white">{selectedSlot.time}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Duration</p>
                      <p className="font-semibold text-slate-900 dark:text-white">{preferences.duration_minutes} minutes</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Type</p>
                      <p className="font-semibold text-slate-900 dark:text-white capitalize">
                        {preferences.appointment_type === 'both' ? 'Telehealth' : preferences.appointment_type}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <Button
                onClick={() => navigate('/patient/dashboard')}
                className="bg-gradient-to-br from-cn-primary to-cn-sage-500 hover:from-cn-primary hover:to-cn-sage-600 text-white rounded-xl px-8 h-12"
              >
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
